import React from 'react';
import Card from '../../Card';
import { getBreakpoint } from '../../../base/style';
import styled from 'styled-components';
import { P2, Header3, Caption, Header5, Header4, P1 } from '../../Typography';
import { getAddressFirstLine } from '../../../utils/common.utils';
import CalendarIcon from '../../../assets/svg/calendar-purple.svg';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getCurrentCustomerId } from '../../../redux/account/account.selectors';
import { OrderWithOrderCustomersDTOStatusIdEnum, ClaimDTOStatusEnum, TenancyDTOTermTypeEnum } from '@reposit/api-client';
import classnames from 'classnames';
import { OrderCustomerEntity, DocumentEntity, TenantEntity } from '../../../redux/entities/entities.types';
import { penceToPounds } from '../../../utils/currency';
import Button from '../../Button/index';
import CardMessage from '../../CardMessage/index';
import DocumentList from '../../DocumentList';
import { get } from 'lodash';
import { isTenancyOrderExpired } from '../../../redux/utils/is-tenancy-order-expired';

interface RepositSummaryProps {
  reposit: any;
}

const List = styled.ul`
  margin: 12px 0 0;

  @media screen and (min-width: ${getBreakpoint('lg')}) {
    display: flex;
  }
`;

const Tenant = styled.li`
  margin: 0;
  margin-right: 33px;

  p {
    margin: 0;
  }
`;

const TenantLabel = styled(Header4)`
  align-items: center;
  display: flex;
  margin: 10px 42px 5px 0;
  padding: 0;
`;

const ChargesLabel = styled(Header4)`
  align-items: center;
  display: flex;
  margin: 10px 42px 5px 0;
  padding: 0;
`;

const SummaryContainer = styled.div`
  padding: 0 5px 10px 0;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    padding: 0 5px 20px 40px;
  }
`;

const ColLeft = styled.div`
  padding: 24px 36px 0;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    padding: 24px 0 0;
  }
`;

const ColRight = styled.div`
  @media all and (min-width: ${getBreakpoint('lg')}) {
    max-width: 280px;
  }
`;

const NoAddress = styled(P2)`
  font-style: italic;
`;

const Address = styled.div`
  margin-bottom: 1rem;
  width: 100%;

  @media screen and (min-width: ${getBreakpoint('lg')}) {
    align-items: center;
    display: flex;
  }
`;

const FirstLineAddress = styled(Header3)`
  line-height: 1.2;
  margin: 0;
  padding: 90;
`;

const TownPostcode = styled(Caption)`
  padding: 2px 0 0;
  @media screen and (min-width: ${getBreakpoint('lg')}) {
    margin: 0 0 0 12px;
  }
`;

const RepositStatus = styled.p`
  border-radius: 8px;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 16px;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.5;
  letter-spacing: -0.2px;
  text-align: center;
  color: #fff;
  padding: 7px 20px 9px;

  &.complete {
    background: ${(props) => props.theme.colors.positive};
  }

  &.pending {
    background: ${(props) => props.theme.colors.warning};
  }

  &.checkedOut {
    background: ${(props) => props.theme.colors.disabled};
  }
`;

const PPM = styled(Header3)`
  align-items: center;
  display: flex;
  margin: 0;
  padding: 0;

  span {
    color: ${(props) => props.theme.colors.placeholder};
    font-family: ${(props) => props.theme.typography.face.secondary};
    font-size: 20px;
    font-weight: normal;
    margin: 0 8px;
  }
`;

const TenancyDates = styled.div`
  margin: 24px 0 20px;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    align-items: center;
    display: flex;
    margin: 0 0 0 24px;
  }
`;

const Label = styled(Header5)`
  align-items: center;
  display: flex;
  margin: 0 14px 0 0;
  padding: 0;

  &:before {
    background: transparent url(${CalendarIcon}) no-repeat center center;
    background-size: 16px;
    content: '';
    display: block;
    height: 20px;
    margin: -3px 10px 0 0;
    width: 20px;
  }
`;

const Dates = styled(P2)`
  color: ${(props) => props.theme.colors.body2};
  margin: 6px 0 0;

  @media all and (min-width: ${getBreakpoint('lg')}) {
    margin: 1px 0 0;
    padding: 0;
  }
`;

const Meta = styled.div`
  @media all and (min-width: ${getBreakpoint('lg')}) {
    align-items: center;
    display: flex;
  }
`;

const RepositSummaryWrapper = styled.div<{ isExpired: boolean; isActionable: boolean }>`
  opacity: ${(props) => (props.isExpired ? 0.4 : 1)};
  cursor: ${(props) => (props.isActionable ? 'pointer' : 'none')};
  pointer-events: ${(props) => (props.isActionable ? 'default' : 'none')};
`;

const Italics = styled.span`
  font-style: italic;
`;

/**
 * Hook that derives the display status and action-required flag for a Reposit.
 *
 * The caller is responsible for resolving and passing the relevant
 * `OrderCustomerEntity` — this makes the resolution logic reusable and allows
 * the component to apply a fallback strategy (see `resolvedOrderCustomer` in
 * `RepositSummary`).
 *
 * The `orderCustomer` parameter is optional; when it is absent (e.g. the
 * current user is not one of the order customers) all `nextAction` checks
 * safely short-circuit via optional chaining, and `isActionRequired` will be
 * `false`.
 *
 * @param reposit - The full Reposit object containing `order` and `tenancy`.
 * @param orderCustomer - The resolved order-customer record for the current
 *   user, or `undefined` if one could not be found.
 * @returns A tuple of `[StatusComponent, isActionRequired]` where
 *   `StatusComponent` is the JSX badge to render and `isActionRequired`
 *   indicates whether the user has a pending next action.
 */
const getRepositStatus = (reposit: any, orderCustomer?: OrderCustomerEntity): [JSX.Element, boolean] => {
  let StatusComponent: JSX.Element;
  let isActionRequired: boolean;
  const { order, tenancy } = reposit;
  const isExpired = isTenancyOrderExpired(reposit);
  const { statusId } = order;
  const repositStatusClass = classnames({
    complete: statusId === OrderWithOrderCustomersDTOStatusIdEnum.COMPLETE && !isExpired,
    pending: statusId !== OrderWithOrderCustomersDTOStatusIdEnum.COMPLETE,
    checkedOut: statusId === OrderWithOrderCustomersDTOStatusIdEnum.COMPLETE && isExpired,
  });

  if (statusId === OrderWithOrderCustomersDTOStatusIdEnum.COMPLETE) {
    if (isExpired) {
      StatusComponent = <RepositStatus className={repositStatusClass}>This Reposit is closed</RepositStatus>;
      isActionRequired = false;
    } else {
      StatusComponent = <RepositStatus className={repositStatusClass}>This Reposit is Active</RepositStatus>;
      isActionRequired = false;
    }
  } else {
    if (tenancy.tenants.length > 1) {
      StatusComponent = (
        <RepositStatus className={repositStatusClass}>
          {orderCustomer?.nextAction ? 'You have outstanding actions' : 'Awaiting other tenants'}
        </RepositStatus>
      );
    } else {
      StatusComponent = (
        <RepositStatus className={repositStatusClass}>
          {orderCustomer?.nextAction ? 'You have outstanding actions' : 'Processing...'}
        </RepositStatus>
      );
    }
    isActionRequired = !!orderCustomer?.nextAction;
  }

  return [StatusComponent, isActionRequired];
};

const filterDocuments = (documents: DocumentEntity[]) => {
  const permittedDocumentTypes = ['SIGNED_ADDENDUM', 'SIGNED_TENANT_TERMS_AND_CONDITIONS'];
  return documents.filter((document: DocumentEntity) => permittedDocumentTypes.includes(document.typeId));
};

const isPeriodicTermType = (termType?: string) => termType === TenancyDTOTermTypeEnum.PERIODIC;

const RepositSummary: React.FC<RepositSummaryProps> = ({ reposit }) => {
  const { order, tenancy } = reposit;
  const { isNewTenancy, documents, tenants } = tenancy;

  const claim = get(tenancy, 'checkout.claim', null);
  let settledCharges;
  if (claim) {
    settledCharges = claim.arbitration ? claim.arbitration.arbitratedAmount : claim.finalAmount;
  }

  const address = tenancy.property.address;
  const customerId = useSelector(getCurrentCustomerId);

  /**
   * Resolves the most relevant `OrderCustomerEntity` to use for status display
   * and checkout navigation, applying a three-tier fallback:
   *
   * 1. The order customer whose `customer.id` matches the currently logged-in
   *    user — the ideal case.
   * 2. Any order customer that has a `nextAction` — handles scenarios where the
   *    current user's ID is not yet populated in Redux (e.g. on initial load).
   * 3. The first order customer in the list — a last-resort fallback so we
   *    always have a valid checkout path rather than a broken link.
   *
   * `canNavigateToCheckout` is derived from whether a customer ID was resolved;
   * if none could be found the CTA and card interactivity are suppressed.
   */
  const resolvedOrderCustomer =
    order.orderCustomers.find((oc: OrderCustomerEntity) => oc.customer.id === customerId) ||
    order.orderCustomers.find((oc: OrderCustomerEntity) => !!oc.nextAction) ||
    order.orderCustomers[0];
  const resolvedCustomerId = resolvedOrderCustomer?.customer?.id;
  const checkoutPath = resolvedCustomerId ? `/checkout/${order.id}/${resolvedCustomerId}` : '/';
  const [StatusComponent, isActionRequired] = getRepositStatus(reposit, resolvedOrderCustomer);
  const isPeriodicTenancy = isPeriodicTermType(tenancy.termType);
  const startDate = tenancy.startDate ? moment(tenancy.startDate).format('Do MMM YYYY') : undefined;
  let tenancyDates: string | undefined;
  let tenancyDatesLabel = isNewTenancy ? 'Tenancy dates' : 'Reposit term';

  if (startDate) {
    if (isPeriodicTenancy) {
      // Periodic tenancies are open-ended, so only show when the term started.
      tenancyDates = startDate;
      tenancyDatesLabel = isNewTenancy ? 'Tenancy start date' : 'Reposit start date';
    } else if (tenancy.endDate) {
      // Fixed-term tenancies have a known end date, so keep showing the full range.
      const endDate = moment(tenancy.endDate).format('Do MMM YYYY');
      tenancyDates = `${startDate} - ${endDate}`;
    }
  }
  const filteredDocuments = filterDocuments(documents);
  const orderIsComplete = order.statusId === OrderWithOrderCustomersDTOStatusIdEnum.COMPLETE;
  const isClaimResolved = claim && claim.status === ClaimDTOStatusEnum.RESOLVED;
  const isExpired = isTenancyOrderExpired(reposit);
  const canNavigateToCheckout = !!resolvedCustomerId;

  const allTenantsHaveNames = tenants.every((tenant: TenantEntity) => !!tenant.firstName && !!tenant.lastName);

  return (
    <RepositSummaryWrapper
      isExpired={isExpired}
      isActionable={canNavigateToCheckout && isActionRequired && !reposit.tenancy.completedAt}
    >
      <Card
        hover
        flush
        style={{ padding: '0 0 1px' }}
        flashMessage={
          isActionRequired && !reposit.tenancy.completedAt && canNavigateToCheckout ? (
            <CardMessage
              title="Please press continue to complete your Reposit"
              type="primary"
              cta={
                <NavLink to={checkoutPath} style={{ textDecoration: 'none' }}>
                  <Button buttonType="white">Continue</Button>
                </NavLink>
              }
            />
          ) : undefined
        }
      >
        <NavLink to={checkoutPath} style={{ textDecoration: 'none' }}>
          <SummaryContainer>
            <ColRight>{StatusComponent}</ColRight>
            <ColLeft>
              {address ? (
                <Address>
                  <FirstLineAddress>{getAddressFirstLine(address)}</FirstLineAddress>
                  <TownPostcode>{address.postcode}</TownPostcode>
                </Address>
              ) : (
                <NoAddress>There isn't a property associated with this Reposit</NoAddress>
              )}
              <Meta>
                <PPM>
                  £{penceToPounds(tenancy.PPM)}
                  <span>pcm</span>
                </PPM>
                {tenancyDates ? (
                  <TenancyDates>
                    <Label>{tenancyDatesLabel}</Label>
                    <Dates>{tenancyDates}</Dates>
                  </TenancyDates>
                ) : null}
              </Meta>
              <TenantLabel>Tenants</TenantLabel>
              <List>
                {tenants &&
                  tenants.map((tenant: TenantEntity) => (
                    <Tenant key={tenant.id}>
                      <P2>{allTenantsHaveNames ? `${tenant.firstName} ${tenant.lastName}` : `${tenant.email}`}</P2>
                    </Tenant>
                  ))}
              </List>
              {isExpired && claim && isClaimResolved ? (
                <>
                  <ChargesLabel>Total charges:</ChargesLabel>
                  <P1>
                    £{penceToPounds(settledCharges)} <Italics>(settled)</Italics>
                  </P1>
                </>
              ) : null}
            </ColLeft>
          </SummaryContainer>
        </NavLink>
        {filteredDocuments.length && orderIsComplete ? <DocumentList rounded documents={filterDocuments(documents)} /> : <></>}
      </Card>
    </RepositSummaryWrapper>
  );
};

export default RepositSummary;
